import { DestroyRef, inject, Injector, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { fromEvent, merge } from 'rxjs';

import { MINUTE, SECOND } from '@constants';
import { UserSettings } from '@models';
import { AppEvent, AppEventType, EventQueueService } from '@services';
import { AppState } from '@store';

@Service()
export class UserSessionService {
  destroyRef = inject(DestroyRef);
  store = inject<Store<AppState>>(Store);
  injector = inject(Injector);

  readonly WARNING_BUFFER = 5 * SECOND;
  readonly GRACE_PERIOD = 3 * MINUTE;
  readonly DEFAULT_TIMEOUT = 30 * MINUTE;
  readonly MIN_TIMER_DURATION = 2000;

  sessionTimer!: number;
  timeout: Nullable<ReturnType<typeof setTimeout>> = null;
  userBean!: UserSettings;
  isLoggedIn = false;
  timerStartTime: Nullable<number> = null;
  expiresAt: Nullable<number> = null;
  logoutAt: Nullable<number> = null;

  constructor() {
    this.store
      .select((state) => state.user)
      .pipe(takeUntilDestroyed())
      .subscribe((userBean) => {
        this.userBean = userBean;
      });

    this.store
      .select((state) => state.auth.isLoggedIn)
      .pipe(takeUntilDestroyed())
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      });

    merge(
      fromEvent(document, 'visibilitychange'),
      fromEvent(window, 'focus'),
      fromEvent(window, 'pageshow')
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.expireIfOverdue(Date.now());
      });

    this.destroyRef.onDestroy(() => {
      this.stopTimer();
    });
  }

  get eventQueueService(): EventQueueService {
    return this.injector.get(EventQueueService);
  }

  startTimer = () => {
    if (!this.isLoggedIn) {
      return;
    }

    const now = Date.now();

    if (this.expireIfOverdue(now) || this.shouldPreventTimerReset(now)) {
      return;
    }

    const baseTimeout = this.userBean?.communityTimeout
      ? Math.trunc(this.userBean.communityTimeout) * MINUTE
      : this.DEFAULT_TIMEOUT;

    this.stopTimer();

    this.sessionTimer = baseTimeout - this.WARNING_BUFFER - this.GRACE_PERIOD;
    this.timerStartTime = now;
    this.expiresAt = now + this.sessionTimer;

    this.scheduleSessionExpiration();
  };

  stopTimer = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    this.timerStartTime = null;
    this.expiresAt = null;
  };

  shouldPreventTimerReset = (now: number) => {
    if (!this.timerStartTime || !this.timeout) {
      return false;
    }

    const timeSinceStart = now - this.timerStartTime;

    return timeSinceStart < this.MIN_TIMER_DURATION;
  };

  expireIfOverdue = (now: number) => {
    if (!this.timeout || this.expiresAt === null || now < this.expiresAt) {
      return false;
    }

    this.expireSession(this.expiresAt);

    return true;
  };

  expireSession = (expiredAt: number) => {
    this.stopTimer();
    this.logoutAt = expiredAt + this.GRACE_PERIOD;
    this.eventQueueService.dispatch(
      new AppEvent(AppEventType.USER_SESSION_EXPIRATION)
    );
  };

  scheduleSessionExpiration = () => {
    const expiresAt = this.expiresAt ?? Date.now();

    this.timeout = setTimeout(() => {
      this.expireSession(expiresAt);
    }, this.sessionTimer);
  };
}
